/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of the centre curve of a ramp, from vpx-js lib/vpt/ramp/ramp-mesh-generator.ts.
// Changes: input is a RampSpec; no texture coordinates; every ramp gives rail paths
// (the scene builder lays a tube along each path) and not a floor or a wall mesh.
// The height of a point is the same as in the upstream physics: the interpolated drag point
// z, plus the linear mix of heightBottom and heightTop along the ramp.

import { CatmullCurve3D } from "../../engine/math/catmull-curve.ts";
import { DragPoint } from "../../engine/math/dragpoint.ts";
import { Vertex2D } from "../../engine/math/vertex2d.ts";
import { RenderVertex3D, type Vertex3D } from "../../engine/math/vertex3d.ts";
import type { RampSpec, Vec3 } from "../../shared/contracts.ts";
import { toDragPoints } from "./shared.ts";

/** One rail of a wire ramp. The points are in the table frame. */
export interface RampRail {
  readonly name: string;
  readonly points: readonly Vec3[];
  /** Radius of the wire. */
  readonly radius: number;
  /** `run` carries the ball. `guide` is a side rail that the player sees through. */
  readonly role?: "run" | "guide";
}

/** Result of the centre line calculation. */
interface RampVertices {
  /** Number of centre points. */
  readonly count: number;
  /** Height at each centre point. */
  readonly heights: number[];
  /** Middle points of the centre line. */
  readonly middle: Vertex2D[];
  /** First half: one side, in order. Second half: the other side, in reverse order. */
  readonly local: Vertex2D[];
}

const WIRE_DIAMETER_DEFAULT = 6;
const WIRE_DISTANCE_X_DEFAULT = 38;

/** The two run rails are this far from the centre line (the ball is 50 wide). */
export const WIRE_RUN_OFFSET = 22;
/** Radius of a run rail. */
export const WIRE_RUN_RADIUS = 3;
/** Radius of a side guide. */
export const WIRE_GUIDE_RADIUS = 2;
/** Lowest height of the top guide, when the spec has no visible wall. */
const WIRE_GUIDE_MIN_HEIGHT = 20;

/**
 * The look of every ramp: a wire ramp. Two thin run rails carry the ball. Two side guides on
 * each side stand at the edge of the ramp, one at the middle and one at the top. There is no
 * floor and no wall, so the player sees the art below. This is a visual only: the collision
 * mesh comes from the spec and does not change.
 */
export function generateWireRamp(spec: RampSpec, tableHeight = 0): RampRail[] {
  const rv = getRampVertices(spec, tableHeight);
  if (rv.count < 2) return [];
  const wall = Math.max(
    spec.leftWallHeightVisible ?? spec.leftWallHeight ?? 0,
    spec.rightWallHeightVisible ?? spec.rightWallHeight ?? 0,
    WIRE_GUIDE_MIN_HEIGHT,
  );
  // A point `offset(half)` units from the centre line, toward side A (+1) or side B (-1).
  const at = (i: number, side: 1 | -1, offset: (half: number) => number, lift: number): Vec3 => {
    const mid = rv.middle[i] as Vertex2D;
    const edge = (side === 1 ? rv.local[i] : rv.local[rv.count * 2 - i - 1]) as Vertex2D;
    const dx = edge.x - mid.x;
    const dy = edge.y - mid.y;
    const half = Math.hypot(dx, dy);
    const k = half > 0 ? offset(half) / half : 0;
    return {
      x: mid.x + dx * k,
      y: mid.y + dy * k,
      z: (rv.heights[i] as number) + lift,
    };
  };
  const rail = (
    name: string,
    role: "run" | "guide",
    side: 1 | -1,
    offset: (half: number) => number,
    lift: number,
    radius: number,
  ): RampRail => {
    const points: Vec3[] = [];
    for (let i = 0; i < rv.count; i++) points.push(at(i, side, offset, lift));
    return { name: `ramp.${name}-${spec.name}`, points, radius, role };
  };
  const run = (half: number): number => Math.min(WIRE_RUN_OFFSET, half * 0.7);
  const full = (half: number): number => half;
  return [
    rail("run1", "run", 1, run, 0, WIRE_RUN_RADIUS),
    rail("run2", "run", -1, run, 0, WIRE_RUN_RADIUS),
    rail("guide1", "guide", 1, full, wall, WIRE_GUIDE_RADIUS),
    rail("guide2", "guide", -1, full, wall, WIRE_GUIDE_RADIUS),
    rail("guide3", "guide", 1, full, wall / 2, WIRE_GUIDE_RADIUS),
    rail("guide4", "guide", -1, full, wall / 2, WIRE_GUIDE_RADIUS),
  ];
}

/** The width of the ramp at a part (0..1) of its length. A wire ramp has a fixed width. */
function rampWidth(spec: RampSpec, percentage: number): number {
  const type = spec.rampType ?? 0;
  if (type === 5) return spec.wireDiameter ?? WIRE_DIAMETER_DEFAULT;
  if (type !== 0) return spec.wireDistanceX ?? WIRE_DISTANCE_X_DEFAULT;
  return percentage * (spec.widthTop - spec.widthBottom) + spec.widthBottom;
}

/** Length of the centre line in the table plane. */
function centreLength(vvertex: readonly RenderVertex3D[]): number {
  let total = 0;
  for (let i = 0; i < vvertex.length - 1; i++) {
    const v1 = vvertex[i] as RenderVertex3D;
    const v2 = vvertex[i + 1] as RenderVertex3D;
    total += Math.hypot(v1.x - v2.x, v1.y - v2.y);
  }
  return total;
}

/**
 * The point where the two edges at a corner meet, after each edge moves outward along its
 * normal. The result is the offset from the corner point.
 */
function cornerNormal(
  vprev: RenderVertex3D,
  vmiddle: RenderVertex3D,
  vnext: RenderVertex3D,
  v1normal: Vertex2D,
  v2normal: Vertex2D,
): Vertex2D {
  const A = vprev.y - vmiddle.y;
  const B = vmiddle.x - vprev.x;
  const C = -(A * (vprev.x - v1normal.x) + B * (vprev.y - v1normal.y));
  const D = vnext.y - vmiddle.y;
  const E = vmiddle.x - vnext.x;
  const F = -(D * (vnext.x - v2normal.x) + E * (vnext.y - v2normal.y));
  const det = A * E - B * D;
  const invDet = det !== 0 ? 1.0 / det : 0.0;
  const intersectX = (B * F - E * C) * invDet;
  const intersectY = (C * D - A * F) * invDet;
  return new Vertex2D(vmiddle.x - intersectX, vmiddle.y - intersectY);
}

/** The side normal of the centre line at point `i`. Ramps do not loop, so the ends are clamped. */
function sideNormal(vvertex: readonly RenderVertex3D[], i: number): Vertex2D {
  const count = vvertex.length;
  const vprev = vvertex[Math.max(i - 1, 0)] as RenderVertex3D;
  const vnext = vvertex[Math.min(i + 1, count - 1)] as RenderVertex3D;
  const vmiddle = vvertex[i] as RenderVertex3D;
  const v1normal = new Vertex2D(vprev.y - vmiddle.y, vmiddle.x - vprev.x);
  const v2normal = new Vertex2D(vmiddle.y - vnext.y, vnext.x - vmiddle.x);

  if (i === count - 1) {
    v1normal.normalize();
    return v1normal;
  }
  if (i === 0) {
    v2normal.normalize();
    return v2normal;
  }
  v1normal.normalize();
  v2normal.normalize();
  const straight =
    Math.abs(v1normal.x - v2normal.x) < 0.0001 && Math.abs(v1normal.y - v2normal.y) < 0.0001;
  return straight ? v1normal : cornerNormal(vprev, vmiddle, vnext, v1normal, v2normal);
}

function getRampVertices(spec: RampSpec, tableHeight: number): RampVertices {
  const vvertex = DragPoint.getRgVertex<RenderVertex3D>(
    toDragPoints(spec.dragPoints),
    () => new RenderVertex3D(),
    (v0, v1, v2, v3) =>
      CatmullCurve3D.fromVertex3D(v0 as Vertex3D, v1 as Vertex3D, v2 as Vertex3D, v3 as Vertex3D),
    false,
    4.0,
  );
  const count = vvertex.length;
  const heights: number[] = [];
  const middle: Vertex2D[] = [];
  const local: Vertex2D[] = [];
  if (count < 2) return { count, heights, middle, local };

  const bottomHeight = spec.heightBottom + tableHeight;
  const topHeight = spec.heightTop + tableHeight;
  const totalLength = centreLength(vvertex);

  let currentLength = 0;
  for (let i = 0; i < count; i++) {
    const vprev = vvertex[Math.max(i - 1, 0)] as RenderVertex3D;
    const vmiddle = vvertex[i] as RenderVertex3D;
    const vnormal = sideNormal(vvertex, i);

    currentLength += Math.hypot(vprev.x - vmiddle.x, vprev.y - vmiddle.y);
    const percentage = totalLength > 0 ? currentLength / totalLength : 0;
    const currentWidth = rampWidth(spec, percentage);
    heights[i] = vmiddle.z + percentage * (topHeight - bottomHeight) + bottomHeight;

    middle[i] = new Vertex2D(vmiddle.x, vmiddle.y);
    local[i] = new Vertex2D(vmiddle.x, vmiddle.y).add(
      vnormal.clone().multiplyScalar(currentWidth * 0.5),
    );
    local[count * 2 - i - 1] = new Vertex2D(vmiddle.x, vmiddle.y).sub(
      vnormal.clone().multiplyScalar(currentWidth * 0.5),
    );
  }
  return { count, heights, middle, local };
}
